/**
 * RetentionZeroWarning and RetentionZeroGatedAction (mission 10 WP15 T088; FR-017 43/4, FR-019 30/11 and 31;
 * UC-04, D.2, NF.9; spec amendment 19; OR-15, U17, U31; style guide §7.1, §7.3, §7.5, §11).
 *
 * The warning states register 37 `retentionZeroWarning` with the periods from the window read's `terms`
 * (`hours` is `zero_fallback_hours`, `minutes` is `sweep_interval_minutes`; FR-022), as a §7.5 warning band
 * carrying `data-requirement="D.2"`. It says deletion waits while a first transmission is pending and can lag
 * by up to one sweep interval (research §1.3, amendment 6; WP15 cycle 2).
 * - `acknowledgment` adds the §7.3 checkbox (`retentionZeroAck`, body text, the whole row a label). Sheets 43
 *   and 30 use it; sheet 31 renders none and passes its second-bureau sentence as `children`.
 * - `inline` renders the same content inside a card with no band: when the page already shows two bands
 *   (T.2 and read-only), a third would break §7.5's limit, and T.2 is never dismissible (U31).
 *
 * The gated action is the button the acknowledgment gates. Unticked, it is a **secondary** button relabelled
 * with the gate (`retentionZeroSaveGated`, `retentionZeroDownloadGated`): §7.1 and §11, "a primary that
 * becomes gated is demoted to secondary and relabeled with the gate". It stays enabled; it runs nothing and
 * moves focus to the acknowledgment. Ticked, it is the primary with its own label and runs the action. It is
 * never a disabled primary: only a typed-confirmation danger button waits disabled for an exact match.
 * The gate is a screen gate only; the server's own refusals (the PATCH's `acknowledge_zero`) are unchanged.
 */
import type { ReactNode } from 'react'
import { TriangleAlert } from 'lucide-react'

import { Band } from '@/components/trellis/Band'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { cn } from '@/components/ui/cn'
import { PROVISIONAL_COPY, copyText } from '@/lib/provisional-copy'

export interface RetentionZeroAcknowledgment {
  /** The checkbox's id: the gated action moves focus here. */
  id: string
  checked: boolean
  onCheckedChange: (checked: boolean) => void
}

export interface RetentionZeroWarningProps {
  /** `terms.zero_fallback_hours` from the window read (FR-022): never a literal. */
  hours: number
  /** `terms.sweep_interval_minutes`: deletion lands up to one sweep interval after it is due (amendment 6). */
  minutes: number
  /** The acknowledgment checkbox (sheets 43 and 30); absent on sheet 31. */
  acknowledgment?: RetentionZeroAcknowledgment
  /** Inside a card, with no band, when two bands already show (U31). */
  inline?: boolean
  /** Further sentences beneath the warning (sheet 31's `retentionZeroSecondBureau`). */
  children?: ReactNode
  className?: string
}

function Acknowledgment({ id, checked, onCheckedChange }: RetentionZeroAcknowledgment) {
  return (
    <div className="mt-2 flex items-start gap-2">
      <Checkbox id={id} className="mt-0.5" checked={checked} onCheckedChange={(next) => onCheckedChange(next === true)} />
      <label htmlFor={id} className="text-body text-navy-950">
        {copyText('retentionZeroAck')}
      </label>
    </div>
  )
}

export function RetentionZeroWarning({ hours, minutes, acknowledgment, inline = false, children, className }: RetentionZeroWarningProps) {
  const body = (
    <>
      <p>{PROVISIONAL_COPY.retentionZeroWarning.render({ hours, minutes })}</p>
      {children}
      {acknowledgment ? <Acknowledgment {...acknowledgment} /> : null}
    </>
  )
  if (inline) {
    return (
      <div data-retention-zero="inline" className={cn('flex gap-2 text-body-sm text-navy-950', className)}>
        <TriangleAlert aria-hidden className="size-5 shrink-0 text-[var(--color-warning-700)]" />
        <div className="min-w-0 flex-1">{body}</div>
      </div>
    )
  }
  return (
    <Band kind="warning" requirement="D.2" data-retention-zero="band" className={className}>
      {body}
    </Band>
  )
}

export type RetentionZeroGate = 'save' | 'download'

const GATE_LABELS = { save: 'retentionZeroSaveGated', download: 'retentionZeroDownloadGated' } as const

export interface RetentionZeroGatedActionProps {
  gate: RetentionZeroGate
  /** The acknowledgment's state. */
  acknowledged: boolean
  /** The acknowledgment checkbox's id, focused when the demoted button is pressed. */
  acknowledgmentId: string
  /** The action, run only once acknowledged. */
  onClick: () => void
  /** The action's own label, shown once acknowledged. */
  children: ReactNode
  loading?: boolean
  className?: string
}

export function RetentionZeroGatedAction({
  gate,
  acknowledged,
  acknowledgmentId,
  onClick,
  children,
  loading = false,
  className,
}: RetentionZeroGatedActionProps) {
  if (acknowledged) {
    return (
      <Button variant="primary" loading={loading} className={className} onClick={onClick}>
        {children}
      </Button>
    )
  }
  return (
    <Button
      variant="secondary"
      data-gated="retention-zero"
      className={className}
      onClick={() => document.getElementById(acknowledgmentId)?.focus()}
    >
      {copyText(GATE_LABELS[gate])}
    </Button>
  )
}
